import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import TriggerOrderDetailsModal from '../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import {
  fetchAccountSettings,
  fetchSingleAccount,
  setCurrentAccount,
} from '../../store/actions/account/accountActions';
import {
  getCurrentAccountUid,
  selectAccounts,
} from '../../store/selectors/accountSelectors';
import { ACCOUNT_UID } from '../../constants/sessionStorage';
import { storeInSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  loadTriggerOrderForDeepLink,
  parseTriggerOrderDeepLink,
  removeTriggerOrderDeepLinkParams,
} from '../../util/helpers/triggerOrderDeepLinkHelper';

// Opens the Buy at Target Price order's details modal when the user arrives via
// a notification link
// (`/portfolio/financial-balances?triggerOrderUid=<uid>&accountUid=<uid>`).
// Auth is handled upstream: PrivateRoute persists the full path (incl. query)
// and the post-login redirect restores it.
const TriggerOrderDeepLink = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const accounts = useSelector(selectAccounts);
  const currentAccountUid = useSelector(getCurrentAccountUid);
  // Read once, on the first render: after a login redirect (which carries router
  // state) PortfolioPage drops the query as it mounts, before the accounts
  // below have loaded.
  const [deepLink] = useState(() => parseTriggerOrderDeepLink(location.search));
  const [order, setOrder] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const handledRef = useRef(false);
  // The account at the time the order arrives, not when the request was sent.
  const currentAccountUidRef = useRef(currentAccountUid);
  currentAccountUidRef.current = currentAccountUid;
  // Not an effect cleanup: the account switch below re-runs the effect, and the
  // order's request has to outlive that.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!deepLink || handledRef.current) return;

    // Wait until accounts are loaded before deciding which one to select. The
    // default account is set before they land (fetchUserAccounts), so the
    // switch below isn't overwritten by it.
    if (!Array.isArray(accounts) || accounts.length === 0) return;

    handledRef.current = true;

    // Strip the link once handled so a refresh or Back doesn't reopen it.
    const search = removeTriggerOrderDeepLinkParams(location.search);
    history.replace(`${location.pathname}${search ? `?${search}` : ''}`);

    const targetAccount = accounts.find(
      (account) =>
        String(account.AccountUid).toLowerCase() ===
        deepLink.accountUid.toLowerCase(),
    );

    // Not one of this user's accounts (another login, a forwarded email, access
    // since revoked): don't open it.
    if (!targetAccount) return;

    loadTriggerOrderForDeepLink({
      accountUid: targetAccount.AccountUid,
      triggerOrderUid: deepLink.triggerOrderUid,
    }).then((result) => {
      // The order isn't on that account (a wrong accountUid): open nothing, and
      // leave the user on the account they are on.
      if (!result || !isMounted.current) return;

      // The same switch as the account menu's, so the page behind the modal
      // shows the order's account.
      if (currentAccountUidRef.current !== targetAccount.AccountUid) {
        storeInSessionStorage(ACCOUNT_UID, targetAccount.AccountUid);
        dispatch(setCurrentAccount(targetAccount));
        dispatch(fetchSingleAccount({ AccountUid: targetAccount.AccountUid }));
        dispatch(
          fetchAccountSettings({ AccountUid: targetAccount.AccountUid }),
        );
      }

      setOrder(result);
      setIsOpen(true);
    });
  }, [
    deepLink,
    accounts,
    location.pathname,
    location.search,
    dispatch,
    history,
  ]);

  return (
    <TriggerOrderDetailsModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      transaction={order?.transaction}
      isEnded={order?.isEnded}
    />
  );
};

export default TriggerOrderDeepLink;
